import {AbstractSubscriptionPayment, SubscriptionConfirmationPayment} from '../subscription.confirmation.payment';
import {Lead} from '../../../../../data/domain/lead';
import {inject, Injectable, PLATFORM_ID} from '@angular/core';
import {SubscriptionResources} from '../../../../../resources/subscription.resources';
import {LeadResources} from '../../../../../resources/lead.resources';
import {RemoteConfig} from '../../../../config/remote-config';
import {createE164PhoneNumber} from '../../../../../common/strings';
import {SubscriptionService} from '../../../../../services/subscription.service';
import {isCompleteFor, isForbiddenAgedClient, isForbiddenPreviousClient} from '../../../../../common/sessions';
import {SocialTrackService} from '../../../../../services/social-track.service';
import {leadToGA, subToGA} from '../../../../../common/ga';
import {MyPaypalTransactionInfo} from '../../../../../model/paypal.model';
import {Router} from '@angular/router';
import {PaymentMode, SubscriptionDTO} from '@minutespourtoi/api-models/dist/models';
import {HttpErrorResponse} from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class PaySubscriptionByPaypal extends AbstractSubscriptionPayment implements SubscriptionConfirmationPayment {

  leadResources = inject(LeadResources)
  remoteConfig = inject(RemoteConfig)
  subscriptionService = inject(SubscriptionService)
  router = inject(Router)

  constructor() {
    super(inject(SubscriptionResources), inject(PLATFORM_ID), inject(SocialTrackService));
  }

  isEligible(): boolean {
    return this.sessionDTO.authorizedPaymentModes?.includes(PaymentMode.card)
      && this.sessionDTO.authorizedPaymentModes?.includes(PaymentMode.paypal)
  }

  async pay(...args: Array<string>): Promise<MyPaypalTransactionInfo> {

    const paymentMode = args[0];
    let paypalTransactionInfoPromise : Promise<MyPaypalTransactionInfo>;

    this.socialTrackService.trackGAEvent(`pay_${paymentMode}`)
    if (this.checkEligibility() && this.checkContext() && this.checkPaymentAndPrice()) {

      const leadDTO = this.leadResources.currentLead.value()
      const isComplete = isCompleteFor(this.sessionDTO, leadDTO)
      const forbiddenAge = isForbiddenAgedClient(this.sessionDTO, leadDTO?.age)
      const isUnauthorized = isForbiddenPreviousClient(this.sessionDTO, this.subscriptionResources.calculatePrice.value()?.oldClient)
      const phoneNumber = this.context.confirmationForm ? createE164PhoneNumber(this.context.confirmationForm.controls.phone.value) : leadDTO.phone

      if (!isComplete && !forbiddenAge && !isUnauthorized) {
        if (this.subscriptionResources.calculatePrice.value()?.totalDiscount === 0 || this.referralCode != null) {
          paypalTransactionInfoPromise = this.doPayByCBOrPaypal(leadDTO, paymentMode)
        } else {
          paypalTransactionInfoPromise = new Promise(async (resolve, reject) => {
            this.context.paymentContext.set({
              authenticationInProgress: true, loading: true, captured: false, error: null, orderCreated: false, loaded: false
            })
            await this.authByPhone.authenticate(phoneNumber, async (isSuccess) => {
              if (isSuccess) {
                this.context.paymentContext.set({
                  authenticationInProgress: false, loading: true, captured: false, error: null, orderCreated: false, loaded: false
                })
                resolve(await this.doPayByCBOrPaypal(leadDTO, paymentMode))
              } else {
                this.actions.failWithMessage("Erreur d'authentification par télephone, veuillez retenter.", null)
                reject()
              }
            })
          })
        }
      } else if (isComplete){
        await this.actions.waitSubscriptionOnCompleteAge()
      } else if(forbiddenAge) {
        await this.actions.waitSubscriptionOnForbiddenAge()
      } else if(isUnauthorized) {
        await this.actions.waitSubscriptionOnUnauthorizedOldClient()
      }
    }

    return paypalTransactionInfoPromise
  }

  private async doPayByCBOrPaypal(leadDTO: Lead, paymentMode: string) {

    const myPaypalTransactionInfo = new MyPaypalTransactionInfo()
    this.context.paymentContext.set({loaded: false, orderCreated: false, loading: true, error: null, captured: false, authenticationInProgress: false})

    try {
      const subscriptionDTO = await this.actions.sendSubscription(leadDTO, paymentMode)
      myPaypalTransactionInfo.orderId = subscriptionDTO.payment?.paypalOrderId
      myPaypalTransactionInfo.price = subscriptionDTO.payment?.amount

      if (!subscriptionDTO.paid) {
        this.context.paymentContext.set({loaded: false, orderCreated: true, loading: false, error: null, captured: false, authenticationInProgress: false})

      } else {
        await this.confirmAndThanks(subscriptionDTO)
      }
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.actions.failWithMessage("Oups, Ton compte a été bloqué lors d'une précédente participation à l'un de nos évenements, tu ne peux donc pas participer à celui-ci. Contacte sebastien@7minutespourtoi.fr pour avoir d'avantage de précisions.", error)
      } else {
        this.actions.failWithMessage("Oups, Une erreur technique est survenue, contacte sebastien@7minutespourtoi.fr pour finaliser ton inscription.", error)
      }
    }

    return myPaypalTransactionInfo
  }

  async capturePayment() {

    try {
      const leadDTO = this.leadResources.currentLead.value();

      this.context.paymentContext.set({loaded: false, orderCreated: true, loading: true, error: null, captured: false, authenticationInProgress: false})

      this.trackBeginCheckout(leadDTO)

      const subscriptionDTO = await this.subscriptionService.captureCheckoutSubscription(leadDTO.subscription.id, leadDTO.subscription.payment.paypalOrderId)

      await this.confirmAndThanks(subscriptionDTO)

    } catch (error) {
      if (error?.status == 412) {
        this.actions.failWithMessage("Nous sommes en attente de reception du paiement de la part de ta banque. Tu recevras un Email de confirmation une fois celui-ci effectué.", error)
      } else if (error?.status == 401) {
        this.actions.failWithMessage("Votre compte a été bloqué suite à un précédent évenement.", error)
      } else {
        this.actions.failWithTechnicalError(error)
      }
    }
  }

  private trackBeginCheckout(leadDTO: Lead) {
    this.socialTrackService.trackBeginCheckout({
      fbData: [leadDTO.sessionId],
      fbValue: leadDTO?.offer?.price ?? 0,
      eventId: leadDTO.sessionId,
      fbDataDetails: [
        {
          id: leadDTO.sessionId,
          quantity: 1,
          item_price: leadDTO?.offer?.price ?? 0
        }
      ],
      gaData: leadToGA(leadDTO),
      tikTokProductId: leadDTO.sessionId,
      tikTokValue: `${leadDTO?.offer?.price ?? 0}`
    })
  }
  private async confirmAndThanks(subscriptionDTO: SubscriptionDTO) {
    if (subscriptionDTO?.paid) {
      this.socialTrackService.trackPurchase({
        fbData: [subscriptionDTO.sessionId],
        eventId: subscriptionDTO.sessionId,
        fbValue: subscriptionDTO?.payment?.amount ?? 0,
        fbDataDetails: [
          {
            id: subscriptionDTO.sessionId,
            quantity: 1,
            item_price: subscriptionDTO?.payment?.amount ?? 0
          }
        ],
        gaData: subToGA(subscriptionDTO),
        tikTokProductId: subscriptionDTO.sessionId,
        tikTokValue: `${subscriptionDTO?.payment?.amount ?? 0}`
      })
      this.context.paymentContext.set({loaded: false, orderCreated: true, loading: false, error: null, captured: true, authenticationInProgress: false})
      await this.router.navigate(['/subscribe-v2', subscriptionDTO.sessionId, 'thanks'])

    } else {
      this.actions.failWithTechnicalError(new Error("Erreur de confirmation du paiement par ta banque."))
    }
  }




}
